import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Stack, Divider,
  List, ListItem, ListItemText, IconButton, Alert, Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { httpGet, httpPost, httpPut, httpDelete } from '../lib/http';

export default function StudyNotes({ pathId, subjectId, topicId, userId }) {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const loadNotes = useCallback(async () => {
    if (!userId || !pathId || !topicId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const res = await httpGet(
        `/api/users/${encodeURIComponent(userId)}/study-notes?pathId=${encodeURIComponent(pathId)}&topicId=${encodeURIComponent(topicId)}`
      );
      
      if (res && res.ok) {
        const data = await res.json();
        setNotes(data.notes || data || []);
      } else {
        setNotes([]);
      }
    } catch (err) {
      console.error('Error loading notes:', err);
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [userId, pathId, topicId]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  async function handleAddNote() {
    if (!newNote.trim() || !userId) return;
    
    setSaving(true);
    setError(null);
    
    try {
      const payload = {
        pathId,
        subjectId,
        topicId,
        content: newNote.trim(),
        createdAt: new Date().toISOString()
      };
      
      const res = await httpPost(
        `/api/users/${encodeURIComponent(userId)}/study-notes`,
        payload
      );
      
      if (res && res.ok) {
        const data = await res.json();
        const savedNote = data.note || data;
        setNotes([...notes, savedNote]);
        setNewNote('');
      } else {
        setError('Failed to save note');
      }
    } catch (err) {
      setError('Error saving note: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdateNote(noteId) {
    if (!editingText.trim() || !userId) return;
    
    setSaving(true);
    setError(null);
    
    try {
      const payload = {
        content: editingText.trim(),
        updatedAt: new Date().toISOString()
      };
      
      const res = await httpPut(
        `/api/users/${encodeURIComponent(userId)}/study-notes/${encodeURIComponent(noteId)}`,
        payload
      );
      
      if (res && res.ok) {
        const data = await res.json();
        const updatedNote = data.note || data;
        setNotes(notes.map(n => n.id === noteId ? updatedNote : n));
        setEditingId(null);
        setEditingText('');
      } else {
        setError('Failed to update note');
      }
    } catch (err) {
      setError('Error updating note: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteNote(noteId) {
    if (!userId) return;
    
    setSaving(true);
    setError(null);
    
    try {
      const res = await httpDelete(
        `/api/users/${encodeURIComponent(userId)}/study-notes/${encodeURIComponent(noteId)}`
      );
      
      if (res && res.ok) {
        setNotes(notes.filter(n => n.id !== noteId));
      } else {
        setError('Failed to delete note');
      }
    } catch (err) {
      setError('Error deleting note: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  }

  function startEditing(note) {
    setEditingId(note.id);
    setEditingText(note.content);
  }

  function cancelEditing() {
    setEditingId(null);
    setEditingText('');
  }

  return (
    <Paper sx={{ 
      p: 3, 
      borderRadius: '16px',
      boxShadow: 'none',
      border: '1px solid',
      borderColor: 'divider'
    }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={700}>
          📝 Study Notes
        </Typography>
        {notes.length > 0 && (
          <Chip 
            label={notes.length} 
            size="small" 
            color="primary"
            sx={{ fontWeight: 800, borderRadius: '16px' }}
          />
        )}
      </Stack>
      
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '16px' }}>{error}</Alert>}
      
      {/* Add new note */}
      <Box sx={{ mb: 3 }}>
        <Stack spacing={1.5}>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="✍️ Jot down key points, questions, or insights from this topic..."
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            disabled={saving}
            sx={{
              '& .MuiOutlinedInput-root': {
                  borderRadius: '16px',
                  bgcolor: 'action.hover'
                }
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              💡 Tip: Write in your own words to remember better
            </Typography>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={handleAddNote}
              disabled={!newNote.trim() || saving}
              sx={{
                borderRadius: '16px',
                fontWeight: 700,
                px: 3,
                textTransform: 'none'
              }}
            >
              {saving ? 'Adding...' : 'Add Note'}
            </Button>
          </Box>
        </Stack>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Notes list */}
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography color="text.secondary">Loading notes...</Typography>
        </Box>
      ) : notes.length === 0 ? (
          <Box sx={{ 
          textAlign: 'center', 
          py: 6,
          px: 2,
          bgcolor: 'action.hover',
          borderRadius: '16px'
        }}>
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            📚 No notes yet
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Start taking notes to enhance your learning!
          </Typography>
        </Box>
      ) : (
        <List sx={{ p: 0 }}>
          {notes.map((note, index) => (
            <ListItem
              key={note.id}
              sx={{
                display: 'block',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '16px',
                mb: 2,
                p: 2.5,
                bgcolor: 'background.paper',
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderColor: 'primary.main'
                },
                transition: 'all 0.2s ease',
                boxShadow: 'none'
              }}
            >
              {editingId === note.id ? (
                <Stack spacing={1}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={editingText}
                    onChange={(e) => setEditingText(e.target.value)}
                    disabled={saving}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '16px' } }}
                  />
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      startIcon={<CancelIcon />}
                      size="small"
                      onClick={cancelEditing}
                      disabled={saving}
                      sx={{ borderRadius: '16px', fontWeight: 700, textTransform: 'none' }}
                    >
                      Cancel
                    </Button>
                    <Button
                      startIcon={<SaveIcon />}
                      variant="contained"
                      size="small"
                      onClick={() => handleUpdateNote(note.id)}
                      disabled={!editingText.trim() || saving}
                      sx={{ borderRadius: '16px', fontWeight: 700, textTransform: 'none' }}
                    >
                      Save
                    </Button>
                  </Stack>
                </Stack>
              ) : (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="body2" sx={{ flex: 1, whiteSpace: 'pre-wrap' }}>
                      {note.content}
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        size="small"
                        onClick={() => startEditing(note)}
                        disabled={saving}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteNote(note.id)}
                        disabled={saving}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(note.createdAt || note.updatedAt).toLocaleString()}
                  </Typography>
                </>
              )}
            </ListItem>
          ))}
        </List>
      )}
    </Paper>
  );
}
